import React, { useEffect, useState } from "react";
import { Badge, Card, Table, Tag, Tooltip } from "antd";
import { errorMessage, warningMessage } from "../../../utils/notificationUtils";
import {
  fetchProcessHistory,
  fetchReportAndStatusData,
} from "../../../apis/reconciliationService";
import dayjs from "dayjs";

interface Props {
  selectedHospital: number | null;
  selectedDate: any;
  onOpenViewMoreModal: () => void;
}

const ReportStatus: React.FC<Props> = ({
  selectedHospital,
  selectedDate,
  onOpenViewMoreModal,
}) => {
  const [isProcessHistoryDataLoading, setIsProcessHistoryDataLoading] =
    useState<boolean>(false);
  const [processHistoryData, setProcessHistoryData] = useState<any>([]);
  const [processHistoryTotal, setProcessHistoryTotal] = useState<number>(0);
  const [isReportAndStatusDataLoading, setIsReportAndStatusDataLoading] =
    useState<boolean>(false);
  const [reportAndStatusData, setReportAndStatusData] = useState<any>([]);
  const [reportAndStatusTotal, setReportAndStatusTotal] = useState<number>(0);
  const [processHistoryPagination, setProcessHistoryPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [reportAndStatusPagination, setReportAndStatusPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const renderStatus = (status: string, lastFetchTime?: string) => {
    const isSuccess = status?.toLowerCase() === "success";
    const color = isSuccess ? "green" : "orange";

    return status ? (
      <Tooltip title={`Last Fetch Time: ${lastFetchTime}` || "-"}>
        <Tag color={color} style={{ borderRadius: "20px", padding: "0 10px" }}>
          <Badge color={color} style={{ marginRight: 6 }} />
          {status}
        </Tag>
      </Tooltip>
    ) : (
      "-"
    );
  };

  const reportAndStatusColumns = [
    {
      title: "Fetched Action",
      dataIndex: "fetchType",
      render: (_: any, record: any) => {
        const value = record?.fetchType;
        return value ? (
          <span style={{ fontWeight: 700 }}>{value}</span>
        ) : (
          (value ?? "-")
        );
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      render: (_: any, record: any) => {
        return record?.date
          ? dayjs(record.date).format("DD-MM-YYYY")
          : "-";
      },
    },
    {
      title: "CGHS NEW",
      dataIndex: "status",
      render: (_: any, record: any) =>
        renderStatus(record.cghsNewStatus, record.cghsNewLastFetchTime),
    },
    {
      title: "ECHS",
      dataIndex: "status",
      render: (_: any, record: any) =>
        renderStatus(record.echsStatus, record.echsLastFetchTime),
    },
  ];

  const processHistoryColumns = [
    {
      title: "Scheme",
      dataIndex: "schemeName",
      render: (_: any, record: any) => {
        const value = record?.schemeName;
        return value ? (
          <span style={{ fontWeight: 700 }}>{value}</span>
        ) : (
          (value ?? "-")
        );
      },
    },
    {
      title: "Fetch Actions",
      dataIndex: "fetchType",
      render: (_: any, record: any) => {
        const value = record?.fetchType;
        return value ? (
          <span style={{ fontWeight: 700 }}>{value}</span>
        ) : (
          (value ?? "-")
        );
      },
    },
    {
      title: "Initiated By",
      dataIndex: "performedByName",
      render: (_: any, record: any) => {
        const value = record?.performedByName;
        return value ? (
          <span style={{ fontWeight: 700 }}>{value}</span>
        ) : (
          (value ?? "-")
        );
      },
    },
    {
      title: "Timestamp",
      dataIndex: "executedAt",
      render: (_: any, record: any) => {
        return record?.executedAt
          ? dayjs.utc(record.executedAt).local().format("DD-MM-YYYY hh:mm A")
          : "-";
      },
    },
    {
      title: "Total Records",
      dataIndex: "processedRecords",
      render: (_: any, record: any) => {
        const value = record?.processedRecords;
        return value ? (
          <span style={{ fontWeight: 700 }}>{value}</span>
        ) : (
          (value ?? "-")
        );
      },
    },
    {
      title: "Processed",
      dataIndex: "processedRecords",
      render: (_: any, record: any) => {
        const value = record?.processedRecords;
        return value ? (
          <span style={{ color: "blue" }}>{value}</span>
        ) : (
          (value ?? "-")
        );
      },
    },
  ];

  const transformData = (data: any[]) => {
    return !data?.length
      ? []
      : data.map((item) => {
          const { date, fetchType, schemeIds } = item;

          return {
            date,
            fetchType,
            echsStatus: schemeIds?.[2]?.status || null,
            echsLastFetchTime: schemeIds?.[2]?.lastFetchTime || null,

            cghsNewStatus: schemeIds?.[96]?.status || null,
            cghsNewLastFetchTime: schemeIds?.[96]?.lastFetchTime || null,
          };
        });
  };

  useEffect(() => {
    getReportAndStatusData();
    getProcessHistoryData();
  }, []);

  const getReportAndStatusData = async (
    page: number = 1,
    size: number = 10,
  ) => {
    try {
      if (!selectedHospital) {
        warningMessage("Please Select Hospital");
        return;
      }
      setIsReportAndStatusDataLoading(true);

      const payload = {
        hospitalId: selectedHospital,
        page: page - 1,
        size,
        startDate: selectedDate
          ? selectedDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS")
          : null,

        endDate: selectedDate
          ? selectedDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS")
          : null,
      };

      const response = await fetchReportAndStatusData(payload);
      const data = transformData(response?.data?.data || []);
      setReportAndStatusData(data);
      setReportAndStatusTotal(response?.data?.totalElements || 0);
    } catch (err) {
      setReportAndStatusData([]);
      setReportAndStatusTotal(0);
      errorMessage(
        (err as any)?.message || "Failed to load report and status list",
      );
    } finally {
      setIsReportAndStatusDataLoading(false);
    }
  };

  const getProcessHistoryData = async (page: number = 1, size: number = 10) => {
    try {
      if (!selectedHospital) {
        warningMessage("Please Select Hospital");
        return;
      }
      setIsProcessHistoryDataLoading(true);

      const payload = {
        hospitalId: selectedHospital,
        page: page - 1,
        size,
      };

      const response = await fetchProcessHistory(payload);
      setProcessHistoryData(response?.data?.data || []);
      setProcessHistoryTotal(response?.data?.totalElements || 0);
    } catch (err) {
      setProcessHistoryData([]);
      setProcessHistoryTotal(0);
      errorMessage((err as any)?.message || "Failed to load process history");
    } finally {
      setIsProcessHistoryDataLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Card>
        <Table
          loading={isReportAndStatusDataLoading}
          columns={reportAndStatusColumns}
          dataSource={reportAndStatusData}
          pagination={{
            current: reportAndStatusPagination.current,
            pageSize: reportAndStatusPagination.pageSize,
            total: reportAndStatusTotal,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onChange: (page, pageSize) => {
              setReportAndStatusPagination({ current: page, pageSize });
              getReportAndStatusData(page, pageSize);
            },
          }}
          rowClassName={(record, index) =>
            index % 2 === 0 ? "ant-table-row-light" : ""
          }
        />
      </Card>

      <Card
        title="Process History"
        extra={<a onClick={onOpenViewMoreModal}>View More</a>}
      >
        <Table
          loading={isProcessHistoryDataLoading}
          columns={processHistoryColumns}
          dataSource={processHistoryData}
          pagination={{
            current: processHistoryPagination.current,
            pageSize: processHistoryPagination.pageSize,
            total: processHistoryTotal,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onChange: (page, pageSize) => {
              setProcessHistoryPagination({ current: page, pageSize });
              getProcessHistoryData(page, pageSize);
            },
          }}
          rowClassName={(record, index) =>
            index % 2 === 0 ? "ant-table-row-light" : ""
          }
        />
      </Card>
    </div>
  );
};

export default ReportStatus;
